(() => {
  'use strict';

  const DATA = window.PAROSICART_VARIANT_MEDIA;
  if (!DATA || !Array.isArray(DATA.media)) return;

  const state = {
    gallery: null,
    variantId: '',
    color: '',
    images: [],
    index: 0,
    timers: []
  };

  const normalize = (value) => String(value || '').trim().toLowerCase();

  const getPicker = () =>
    document.querySelector('main variant-picker[data-template-product-match="true"]') ||
    document.querySelector('variant-picker[data-template-product-match="true"]');

  const getSelectedVariantId = () => {
    const picker = getPicker();
    if (!picker) return '';

    const jsonScript = picker.querySelector('script[type="application/json"]');
    if (jsonScript?.textContent) {
      try {
        const variant = JSON.parse(jsonScript.textContent);
        if (variant?.id) return String(variant.id);
      } catch (_) {}
    }

    const selected = picker.querySelector('fieldset input:checked, select option:checked, select option[selected]');
    return selected?.dataset?.variantId ? String(selected.dataset.variantId) : '';
  };

  const getColorValueFromPicker = () => {
    const picker = getPicker();
    if (!picker) return '';

    const select = picker.querySelector('select[name="options[Color]"], select[name="options[Colour]"]');
    if (select?.value) return String(select.value).trim();

    const radio = Array.from(picker.querySelectorAll('input[type="radio"][data-option-name]'))
      .find((input) => input.checked && /^(color|colour)$/i.test(String(input.dataset.optionName || '').trim()));
    if (radio?.value) return String(radio.value).trim();

    const variantId = getSelectedVariantId();
    const variant = Array.isArray(DATA.variants)
      ? DATA.variants.find((item) => String(item.id) === variantId)
      : null;

    if (variant && Array.isArray(variant.options)) {
      const colorControls = Array.from(picker.querySelectorAll('input[data-option-name], select'))
        .map((node) => String(node.dataset?.optionName || node.name || '').replace(/^options\[|\]$/g, ''))
        .map(normalize);
      const colorIndex = colorControls.findIndex((name) => name === 'color' || name === 'colour');
      if (colorIndex >= 0 && variant.options[colorIndex]) return String(variant.options[colorIndex]);
    }

    return '';
  };

  const matchesColor = (media, color) => {
    const wanted = normalize(color);
    if (!wanted) return false;
    const haystack = normalize(`${media.alt || ''} ${media.url || ''}`);
    return !!haystack && haystack.includes(wanted);
  };

  const getImagesForVariantBlock = (variant, images) => {
    const startMediaId = variant?.featuredMediaId ? String(variant.featuredMediaId) : '';
    if (!startMediaId || !Array.isArray(DATA.variants)) return [];

    const start = images.findIndex((media) => String(media.id) === startMediaId);
    if (start < 0) return [];

    const starts = DATA.variants
      .map((item) => item?.featuredMediaId ? String(item.featuredMediaId) : '')
      .filter(Boolean)
      .map((mediaId) => images.findIndex((media) => String(media.id) === mediaId))
      .filter((index) => index >= 0)
      .sort((a, b) => a - b);

    const nextStart = starts.find((index) => index > start);
    return images.slice(start, nextStart ?? images.length);
  };

  const getImagesForSelection = () => {
    const images = DATA.media.filter((media) => media.type === 'image' && media.url);
    if (!images.length) return [];

    const variantId = getSelectedVariantId();
    const variant = variantId && Array.isArray(DATA.variants)
      ? DATA.variants.find((item) => String(item.id) === variantId)
      : null;

    // Important: Shopify may attach only the first image of a color to its variant.
    // Treat the ordered product-media block between this variant's featured image
    // and the next variant's featured image as the full image set for that color.
    if (variant) {
      const block = getImagesForVariantBlock(variant, images);
      if (block.length) return block;

      const variantMatches = images.filter((media) =>
        Array.isArray(media.variantIds) && media.variantIds.some((id) => String(id) === variantId)
      );
      if (variantMatches.length) return variantMatches;
    }

    const color = getColorValueFromPicker();
    if (color) {
      const exactMatches = images.filter((media) => matchesColor(media, color));
      if (exactMatches.length) return exactMatches;

      const colors = Array.isArray(DATA.colors) ? DATA.colors : [];
      const colorIndex = colors.findIndex((value) => normalize(value) === normalize(color));
      if (colorIndex >= 0 && colors.length > 0 && images.length % colors.length === 0) {
        const perColor = images.length / colors.length;
        return images.slice(colorIndex * perColor, (colorIndex + 1) * perColor);
      }
    }

    if (variant?.featuredMediaId) {
      const featured = images.filter((media) => String(media.id) === String(variant.featuredMediaId));
      if (featured.length) return featured;
    }

    return [];
  };

  const setCurrent = (index) => {
    if (!state.images.length) return;
    state.index = Math.max(0, Math.min(index, state.images.length - 1));
    render();
  };

  const render = () => {
    const gallery = state.gallery;
    if (!gallery) return;

    const main = gallery.querySelector('[data-parosicart-main-image]');
    const thumbs = gallery.querySelector('[data-parosicart-thumbs]');
    if (!main || !thumbs) return;

    thumbs.replaceChildren();

    if (!state.images.length) {
      main.removeAttribute('src');
      main.alt = '';
      gallery.classList.remove('has-multiple-images');
      gallery.classList.add('is-ready');
      return;
    }

    const current = state.images[state.index] || state.images[0];
    main.src = current.url;
    main.alt = current.alt || '';

    state.images.forEach((media, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'parosicart-variant-gallery__thumb';
      button.setAttribute('role', 'listitem');
      button.setAttribute('aria-current', index === state.index ? 'true' : 'false');
      button.setAttribute('aria-label', `View image ${index + 1}`);

      const img = document.createElement('img');
      img.src = media.url;
      img.alt = media.alt || '';
      img.loading = 'lazy';
      button.appendChild(img);

      button.addEventListener('click', () => setCurrent(index));
      thumbs.appendChild(button);
    });

    gallery.classList.toggle('has-multiple-images', state.images.length > 1);
    gallery.classList.add('is-ready');
  };

  const refresh = () => {
    const variantId = getSelectedVariantId();
    const color = getColorValueFromPicker();
    const images = getImagesForSelection();

    if (!variantId && !color) return;

    const selectionChanged =
      String(variantId || '') !== String(state.variantId || '') ||
      normalize(color) !== normalize(state.color);

    state.variantId = variantId;
    state.color = color;
    state.images = images;
    if (selectionChanged || state.index >= images.length) state.index = 0;
    render();
  };

  const moveIntoNativeGalleryPosition = () => {
    const custom = document.querySelector('[data-parosicart-gallery]');
    if (!custom) return false;

    const nativeGallery = document.querySelector('main media-gallery') || document.querySelector('media-gallery');
    if (!nativeGallery) return false;

    if (!custom.classList.contains('parosicart-gallery-positioned')) {
      nativeGallery.replaceWith(custom);
      custom.classList.add('parosicart-gallery-positioned');
    }

    state.gallery = custom;
    return true;
  };

  const scheduleRefresh = () => {
    state.timers.forEach((timer) => clearTimeout(timer));
    state.timers = [
      setTimeout(refresh, 0),
      setTimeout(refresh, 80),
      setTimeout(refresh, 250),
      setTimeout(refresh, 600)
    ];
  };

  const init = () => {
    if (!moveIntoNativeGalleryPosition()) return false;

    const picker = getPicker();
    if (picker && !picker.dataset.parosicartGalleryBound) {
      picker.dataset.parosicartGalleryBound = 'true';
      picker.addEventListener('change', scheduleRefresh);

      const observer = new MutationObserver(scheduleRefresh);
      observer.observe(picker, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['checked', 'data-current-checked', 'value', 'selected']
      });
    }

    refresh();
    return true;
  };

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-parosicart-prev]')) {
      event.preventDefault();
      if (state.images.length > 1) {
        setCurrent((state.index - 1 + state.images.length) % state.images.length);
      }
    }

    if (target.closest('[data-parosicart-next]')) {
      event.preventDefault();
      if (state.images.length > 1) {
        setCurrent((state.index + 1) % state.images.length);
      }
    }
  });

  const boot = () => {
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (init() || attempts >= 30) clearInterval(timer);
    }, 100);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
